import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, RichFAQ, RelatedLinks, RichCTA,
} from '../RichBlocks';
import { EVChargingCalculator, PriceBreakdownBar, SmartChargeTimeline } from '../RichCalculators';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

export default function OpladningHjemmeHovedstaden({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    { question: 'Hvad koster det at oplade elbil hjemme i Hovedstaden?', answer: 'Hovedstaden ligger i prisområde DK2. Den samlede kWh-pris til hjemmeopladning ligger typisk på 1,5–2,5 kr./kWh inkl. transport, afgift og moms – men med Saving Mode (opladning i de billige timer) og 10 øre/kWh laderabat kan den effektive pris blive lavere. For en typisk pendler bliver det ca. 2.500–4.500 kr./år.' },
    { question: 'Hvorfor er hjemmeopladning billigere end offentlig ladning?', answer: 'Hjemme lader du med AC til spotpris plus de faste afgifter. Offentlige hurtigladere (DC) koster ofte 4–7 kr./kWh, fordi de inkluderer dyr infrastruktur og en avance. Hjemmeopladning til spotpris uden tillæg er derfor langt den billigste hverdagsløsning.' },
    { question: 'Hvad er den billigste tid at lade på i Hovedstaden?', answer: 'Strømmen er typisk billigst om natten (ca. kl. 00–06) og dyrest i aftenspidsen (ca. kl. 17–20). Saving Mode flytter automatisk din opladning til de billigste timer i DK2, så du ikke behøver at holde øje selv.' },
    { question: 'Kan jeg få elafgift-refusion på hjemmeopladning?', answer: 'Ja. Som elbilejer kan du få en stor del af elafgiften refunderet på den strøm, du lader bilen med hjemme. Hos enkel energi søger vi automatisk refusionen for dig som en del af serviceaftalen.' },
    { question: 'Hvad koster en kWh i Hovedstaden i 2026?', answer: 'Den samlede pris for en husstand i DK2 ligger typisk på ca. 2,2–2,6 kr./kWh inkl. alt. Den rå spotpris er kun ca. 0,7–1,2 kr./kWh; resten er nettarif, elafgift og moms. Hos enkel betaler du spotpris + 0 øre tillæg.' },
    { question: 'Skal jeg have en ladeboks for at lade hjemme?', answer: 'For sikker og hurtig hjemmeopladning anbefales en fast ladeboks installeret af en autoriseret elinstallatør. Den giver op til 22 kW, virker med Saving Mode og udløser laderabatten. Ladeboks fra 3.499 kr. + installation fra 1.300 kr.' },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'sammensaetning', label: 'Hvad du betaler for' },
    { id: 'timer', label: 'De billige timer' },
    { id: 'beregner', label: 'Beregn din pris' },
    { id: 'spar', label: 'Sådan sparer du' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Hvad koster opladning hjemme i Hovedstaden', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Hvad koster opladning hjemme i Hovedstaden 2026" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="El forklaret · Hovedstaden (DK2) · 2026"
        h1="Hvad koster det at oplade elbil hjemme i Hovedstaden?"
        intro="Et klart svar på, hvad hjemmeopladning af elbil koster i Hovedstaden (DK2) i 2026 – og hvordan du gør den så billig som muligt med spotpris uden tillæg, Saving Mode og laderabat."
        badges={['Hovedstaden = DK2', 'Spotpris + 0 øre', 'Elafgift-refusion inkl.']}
        updated={updated}
        image="https://images.unsplash.com/photo-1593941707882-a5bba14938c7?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '~2,2–2,6 kr.', label: 'samlet kWh-pris i DK2' },
          { value: '~0,7–1,2 kr.', label: 'ren spotpris (Nord Pool)' },
          { value: '~2.500–4.500 kr.', label: 'opladning/år (typisk pendler)' },
          { value: '10 øre/kWh', label: 'laderabat hos enkel' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Hvad koster hjemmeopladning i Hovedstaden?">
        <AnswerBox question="Hvad koster det at oplade elbil hjemme i Hovedstaden i 2026?">
          Hovedstaden ligger i prisområde <strong>DK2</strong>. Den samlede kWh-pris til hjemmeopladning ligger typisk på
          <strong> 1,5–2,5 kr./kWh</strong> inkl. transport, afgift og moms – men med Saving Mode (opladning i de billige timer) og
          <strong> 10 øre/kWh laderabat</strong> (på opladning via Saving Mode med kompatibel ladeboks) bliver din effektive pris lavere. For en typisk pendler bliver det ca. <strong>2.500–4.500 kr./år</strong>.
          Det er langt billigere end offentlig hurtigladning (4–7 kr./kWh).
        </AnswerBox>
        <p>
          Hjemmeopladning er den billigste måde at lade elbil på – men kun hvis du lader rigtigt. Det handler om at vælge et selskab uden tillæg,
          lade i de billige timer og udnytte laderabat og elafgift-refusion.
        </p>
      </ProseSection>

      <ProseSection id="sammensaetning" heading="Hvad betaler du egentlig for?" alt>
        <p>Din kWh-pris består af fire dele. Kun spotprisen svinger – resten er faste afgifter, der er ens uanset elselskab:</p>
        <div className="my-6"><PriceBreakdownBar /></div>
        <p>Hos enkel energi lægger vi 0 øre i tillæg til spotprisen. Du betaler altså kun den rå markedspris for DK2 plus de faste afgifter og et fast abonnement fra 18 kr./md.</p>
      </ProseSection>

      <ProseSection id="timer" heading="Lad i de billige timer – automatisk">
        <p>I DK2 er strømmen typisk billigst om natten og dyrest i aftenspidsen. Saving Mode flytter automatisk din opladning til de billigste timer:</p>
        <div className="my-6"><SmartChargeTimeline /></div>
      </ProseSection>

      <ProseSection id="beregner" heading="Beregn din egen opladningspris" alt>
        <div className="my-2"><EVChargingCalculator defaultKm={15000} /></div>
      </ProseSection>

      <ProseSection id="spar" heading="Tre ting der gør hjemmeopladning billigst">
        <FactGrid columns={3} facts={[
          { value: '0 øre', label: 'Tillæg til spotpris', sub: 'Vælg et selskab uden tillæg' },
          { value: '15–30%', label: 'Saving Mode-besparelse', sub: 'Historisk, ikke garanteret' },
          { value: '10 øre/kWh', label: 'Laderabat + refusion', sub: 'Via Saving Mode' },
        ]} />
        <AnswerBox>
          Kombinationen er nøglen: spotpris uden tillæg + Saving Mode + 10 øre/kWh laderabat + automatisk elafgift-refusion. For mange pendlere
          i Hovedstaden dækker laderabatten reelt hele el-abonnementet, så hjemmeopladningen bliver så billig som overhovedet muligt.
        </AnswerBox>
      </ProseSection>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/sammenlign/elbil-budget-hovedstaden', label: 'Elbil på budget i Hovedstaden' },
        { path: '/sammenlign/hvad-er-dk1-dk2', label: 'Hvad er DK1 og DK2?' },
        { path: '/pris-time-for-time', label: 'Elpris time for time' },
        { path: '/el-fordele#laderabat', label: 'Laderabat' },
        { path: '/portal/smart-charge', label: 'Saving Mode' },
        { path: '/elaftale', label: 'Se el-aftaler' },
      ]} />

      <RichCTA headline="Oplad billigt hjemme i Hovedstaden" subtext="Spotpris uden tillæg, Saving Mode og laderabat. Fra 18 kr./md., ingen binding." />
      <SEODisclaimer page={page} />
    </article>
  );
}